/**
 * Scroll-revealed customer-service bar carrying the travel-claims assistance line.
 *
 * The coordinates it prints are NOT this component's to decide (DEV-7932): it renders whatever
 * `resolveSupportContact` hands it, and renders nothing at all when that answers `null`. It used
 * to print one insurer's travel-claims line on every tenant, including the ones served through
 * the shared generic portal shell, where that line reaches a department that cannot open the
 * caller's file. See `utils/supportContact` for the rule and why an absent answer is not a gap
 * to fill.
 *
 * The two `footer.*` keys left here deliberately carry NO inline i18n default any more, and the
 * two that named the coordinates are no longer read at all: their inline defaults hardcoded the
 * number and the option in this file, so a missing or renamed translation key reprinted them
 * verbatim — the hidden second source the whole story exists to remove. The coordinates now
 * arrive from the resolver as data; only the prose around them is translated.
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Phone } from 'lucide-react';
import { useEmbeddedApp } from '../providers/EmbeddedAppProvider';
import { resolveSupportContact } from '../utils/supportContact';

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const { tenantConfig } = useEmbeddedApp();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Calculate if user is at the bottom of the page
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;

      // Show footer when within 100px of bottom
      const threshold = 100;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - threshold;

      setIsVisible(isAtBottom);
    };

    // Check on mount
    handleScroll();

    // Add scroll listener
    window.addEventListener('scroll', handleScroll);

    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const supportContact = resolveSupportContact(tenantConfig);

  // After the hooks, never between them: the scroll listener above must be registered and torn
  // down on the same schedule whatever the tenant answers, so the resolver cannot change how
  // many hooks this render calls.
  if (!supportContact) {
    return null;
  }

  return (
    <footer
      className={`fixed bottom-0 left-0 right-0 bg-gray-50 border-t border-gray-200 py-4 print:hidden z-10 transition-all duration-300 ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center text-center space-y-2">
          <div className="flex items-center space-x-2">
            <Phone className="w-5 h-5 text-gray-600" />
            <p className="text-sm font-medium text-gray-900">
              {t('footer.needAssistance')}
            </p>
          </div>
          <p className="text-sm text-gray-700">
            {t('footer.contactInfo')}{' '}
            <span className="font-semibold text-gray-900">
              {supportContact.phone}
            </span>{' '}
            <span className="text-gray-600">
              {supportContact.option}
            </span>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
